import { SnackbarProvider } from "notistack";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import DraftBookingApplier from "@/components/DraftBookingApplier";
import FeaturesPanel from "@/components/FeaturesPanel";
import { AppConfigProvider } from "@/contexts/AppConfigContext";
import { BookingProvider } from "@/contexts/BookingContext";
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import FAQ from "@/features/faq/FAQ";
import Imprint from "@/features/imprint/Imprint";
import Install from "@/features/install/Install";
import PrivacyPolicy from "@/features/privacy-policy/PrivacyPolicy";
import { analytics } from "@/utils/analytics";
import { AppSnackbar } from "./components/AppSnackbar";
import ErrorBoundary from "./components/ErrorBoundary";
import Confirmation from "./features/booking/Confirmation";
import BookingLayout from "./features/booking/components/BookingLayout";
import Details from "./features/booking/Details";
import Extras from "./features/booking/Extras";
import Payment from "./features/booking/Payment";
import StripePayment from "./features/booking/StripePayment";
import Accommodation from "./features/search/Accommodation";
import SearchLayout from "./features/search/components/SearchLayout";
import Dates from "./features/search/Dates";
import Guests from "./features/search/Guests";
import Results from "./features/search/Results";

function RedirectWithSearch({ to }: { to: string }) {
  const { search } = useLocation();
  if (new URLSearchParams(search).has("draftbooking")) return null;
  return <Navigate to={`${to}${search}`} replace />;
}

function PostHogPageview() {
  const location = useLocation();
  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not needed in the body
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

const AppWrapper = () => (
  <>
    <PostHogPageview />
    <ScrollToTop />
    <AppConfigProvider>
      <FeaturesProvider>
        <BookingProvider>
          <DraftBookingApplier />
          <SnackbarProvider
            maxSnack={3}
            autoHideDuration={4000}
            preventDuplicate
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            Components={{
              error: AppSnackbar,
              success: AppSnackbar,
              warning: AppSnackbar,
              info: AppSnackbar,
            }}
          >
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
            <FeaturesPanel />
          </SnackbarProvider>
        </BookingProvider>
      </FeaturesProvider>
    </AppConfigProvider>
  </>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path=":language/:campground/" element={<AppWrapper />}>
          <Route index element={<RedirectWithSearch to="search/dates" />} />
          <Route path="search" element={<SearchLayout />}>
            <Route index element={<RedirectWithSearch to="dates" />} />
            <Route path="dates" element={<Dates />} />
            <Route path="accommodation" element={<Accommodation />} />
            <Route path="guests" element={<Guests />} />
          </Route>
          <Route path="search/results" element={<Results />} />
          <Route path="booking" element={<BookingLayout />}>
            <Route path="extras" element={<Extras />} />
            <Route path="details" element={<Details />} />
            <Route path="payment" element={<Payment />} />
            <Route path="payment-stripe" element={<StripePayment />} />
          </Route>
          <Route path="booking/confirmation/:bookingId?" element={<Confirmation />} />
          <Route path="install" element={<Install />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="imprint" element={<Imprint />} />
          <Route path="*" element={<Navigate to=".." replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
